import {
  AvatarDirectory,
  EMPTY_AVATAR_DIRECTORY,
  buildAvatarDirectory,
} from './avatarDirectory';

/**
 * The organization's people, indexed by the two handles the admin screens need.
 *
 * `/admin/users` answers with the NUMERIC id and nothing else that identifies a
 * person outside the database - no uuid, under any spelling. But a URL should
 * not carry a row number: `/settings/admin-users/48` leaks how many people the
 * table has ever held and means nothing if the record is ever re-keyed.
 *
 * `/org-people/people` is where the uuid lives (`id` is the person's uuid,
 * `user_id` the numeric one), and the admin pages already fetch that payload
 * for the profile pictures. So the same request answers both questions: which
 * picture a row wears, and which uuid its URL should use.
 *
 * Pure and both-ways: the list turns an id into a uuid for the link, the detail
 * turns a uuid back into an id for the lookup, and neither has to know how the
 * other did it.
 */

export interface PeopleDirectory {
  /** Pictures, keyed as `avatarDirectory` describes. */
  readonly avatars: AvatarDirectory;
  /** Numeric user id → uuid. The link direction. */
  readonly uuidByUserId: ReadonlyMap<number, string>;
  /** Lower-cased uuid → numeric user id. The lookup direction. */
  readonly userIdByUuid: ReadonlyMap<string, number>;
}

export const EMPTY_PEOPLE_DIRECTORY: PeopleDirectory = {
  avatars: EMPTY_AVATAR_DIRECTORY,
  uuidByUserId: new Map<number, string>(),
  userIdByUuid: new Map<string, number>(),
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether a route segment is a uuid rather than a numeric id.
 *
 * Used to decide whether an unresolved id is worth WAITING for - a uuid can
 * only be resolved once the directory has arrived, a number never needs it.
 */
export const isUuidLike = (value: unknown): boolean =>
  typeof value === 'string' && UUID_PATTERN.test(value.trim());

/** Both spellings of the numeric id `/org-people/people` may carry. */
const numericId = (person: any): number | null => {
  const raw = person?.user_id ?? person?.userId;
  const parsed = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Index a `/org-people/people` payload.
 *
 * `id` is the uuid there - except when the person has none, where the backend
 * falls back to the stringified numeric id. That fallback is deliberately NOT
 * indexed: a "uuid" that is really a row number would put the row number back
 * in the URL under a name that says it is not one.
 *
 * First writer wins, as in `buildAvatarDirectory` - the payload is ordered by
 * the backend and a later duplicate must not take over an earlier person's key.
 */
export const buildPeopleDirectory = (people: any[] | null | undefined): PeopleDirectory => {
  const uuidByUserId = new Map<number, string>();
  const userIdByUuid = new Map<string, number>();

  for (const person of people || []) {
    const uuid = typeof person?.id === 'string' ? person.id.trim() : '';
    const id = numericId(person);
    if (!isUuidLike(uuid) || id === null) continue;
    if (!uuidByUserId.has(id)) uuidByUserId.set(id, uuid);
    const key = uuid.toLowerCase();
    if (!userIdByUuid.has(key)) userIdByUuid.set(key, id);
  }

  return { avatars: buildAvatarDirectory(people), uuidByUserId, userIdByUuid };
};
